import React from "react";

const OCCASION_TITLES = {
  birthday: "Gratulerer med dagen!",
  christmas: "God Jul!",
  easter: "God Påske!",
  mothersday: "Gratulerer med Morsdagen!",
  womensday: "Gratulerer med Kvinnedagen!",
  occasion: "Gavekort",
  none: "Gavekort",
};

// Warm accent colors matching the spice background
const OCCASION_COLORS = {
  birthday: { border: "#E8C86A", title: "#FFD700" },
  christmas: { border: "#C84040", title: "#E85050" },
  easter: { border: "#D4A847", title: "#E8D070" },
  mothersday: { border: "#D4A847", title: "#F0C060" },
  womensday: { border: "#D4A847", title: "#F0C060" },
};

export const GiftCardPreview = ({ formData }) => {
  const { amount, sender_name, recipient_name, occasion } = formData;
  const colors = OCCASION_COLORS[occasion];
  const borderColor = colors?.border || "#D4A847";
  const titleColor = colors?.title || "#E8C86A";
  const title = OCCASION_TITLES[occasion] || "Gavekort";

  return (
    <div data-testid="giftcard-preview" className="w-full">
      <h3
        className="text-sm font-semibold mb-3 uppercase tracking-wider text-center"
        style={{ color: "#8b4513" }}
      >
        Forhåndsvisning
      </h3>

      {/* Card — landscape ratio */}
      <div
        className="relative overflow-hidden mx-auto"
        style={{
          aspectRatio: "1.414 / 1",
          maxWidth: 420,
          borderRadius: 8,
          boxShadow: "0 8px 32px rgba(0,0,0,0.35)",
        }}
      >
        {/* Website hero background */}
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1716816211590-c15a328a5ff0?w=800')`,
          }}
        />

        {/* Dark overlay */}
        <div
          className="absolute inset-0"
          style={{ background: "rgba(12,4,2,0.68)" }}
        />

        {/* Outer border */}
        <div
          className="absolute"
          style={{
            inset: 6,
            border: `2.5px solid ${borderColor}`,
            borderRadius: 4,
          }}
        />
        {/* Inner border */}
        <div
          className="absolute"
          style={{
            inset: 12,
            border: `0.5px solid ${borderColor}30`,
            borderRadius: 3,
          }}
        />

        {/* Corner ornaments */}
        {[
          { top: 14, left: 14 },
          { top: 14, right: 14 },
          { bottom: 14, left: 14 },
          { bottom: 14, right: 14 },
        ].map((pos, i) => (
          <div
            key={i}
            className="absolute"
            style={{
              ...pos,
              width: 16,
              height: 16,
              borderTop: i < 2 ? "1px solid #A08030" : "none",
              borderBottom: i >= 2 ? "1px solid #A08030" : "none",
              borderLeft: i % 2 === 0 ? "1px solid #A08030" : "none",
              borderRight: i % 2 === 1 ? "1px solid #A08030" : "none",
            }}
          />
        ))}

        {/* Content */}
        <div className="relative z-10 flex flex-col items-center justify-between h-full px-5 py-4">
          {/* Restaurant name */}
          <div className="text-center">
            <h2
              className="font-serif italic font-bold leading-tight"
              style={{
                color: titleColor,
                fontSize: "clamp(14px, 4vw, 22px)",
                textShadow: "0 1px 8px rgba(0,0,0,0.5)",
              }}
            >
              Masala Indisk Restaurant og Bar
            </h2>
            <div
              className="mx-auto my-1"
              style={{ width: "75%", height: 1, background: borderColor }}
            />
            <p
              className="italic"
              style={{ color: "#D4C4A0", fontSize: "clamp(7px, 2vw, 10px)" }}
            >
              Smak av India
            </p>
          </div>

          {/* Ornament + Title */}
          <div className="text-center w-full" style={{ margin: "-2px 0" }}>
            <div className="flex items-center justify-center gap-2 mb-1">
              <div style={{ height: 0.5, flex: 1, maxWidth: 45, background: `${borderColor}80` }} />
              <div
                style={{
                  width: 5, height: 5, background: borderColor,
                  transform: "rotate(45deg)",
                }}
              />
              <div style={{ height: 0.5, flex: 1, maxWidth: 45, background: `${borderColor}80` }} />
            </div>
            <p
              className="font-serif italic font-bold"
              style={{
                color: titleColor,
                fontSize: "clamp(11px, 3vw, 15px)",
              }}
            >
              {title}
            </p>
          </div>

          {/* Amount */}
          <div
            className="flex items-center justify-center"
            style={{
              border: `1.5px solid ${borderColor}`,
              borderRadius: 6,
              padding: "5px 22px",
              background: "rgba(18,5,2,0.5)",
            }}
          >
            <span
              className="font-bold text-white"
              style={{ fontSize: "clamp(20px, 5.5vw, 28px)" }}
            >
              kr {amount || 0},-
            </span>
          </div>

          {/* Ornament */}
          <div className="flex items-center justify-center gap-2">
            <div style={{ height: 0.5, flex: 1, maxWidth: 35, background: "#A0803080" }} />
            <div style={{ width: 4, height: 4, background: "#A08030", transform: "rotate(45deg)" }} />
            <div style={{ height: 0.5, flex: 1, maxWidth: 35, background: "#A0803080" }} />
          </div>

          {/* FRA / TIL */}
          <div className="w-full flex justify-between px-3">
            <div>
              <p style={{ color: "#D4A847", fontSize: "clamp(6px, 1.8vw, 8px)" }}>FRA</p>
              <p
                className="font-bold text-white truncate"
                style={{ fontSize: "clamp(10px, 2.8vw, 14px)", maxWidth: 120 }}
              >
                {sender_name || "Ditt navn"}
              </p>
            </div>
            <div className="text-right">
              <p style={{ color: "#D4A847", fontSize: "clamp(6px, 1.8vw, 8px)" }}>TIL</p>
              <p
                className="font-bold text-white truncate"
                style={{ fontSize: "clamp(10px, 2.8vw, 14px)", maxWidth: 120 }}
              >
                {recipient_name || "Mottakers navn"}
              </p>
            </div>
          </div>

          {/* Contact + Website at bottom */}
          <div className="text-center">
            <p style={{ color: "rgba(212,168,71,0.55)", fontSize: "clamp(5px, 1.4vw, 7px)" }}>
              Rikard Kaarbos plass 1, 9405 Harstad &bull; +47 489 15 190
            </p>
            <p
              className="font-semibold"
              style={{
                color: "rgba(212,168,71,0.8)",
                fontSize: "clamp(6px, 1.6vw, 8px)",
                marginTop: 1,
              }}
            >
              www.masalaindisk.no
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
